import React, { useState, useId } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  HelpCircle,
  ChevronDown,
  Search,
  Sparkles,
  BookOpen,
  QrCode,
  ShieldCheck,
  Building,
  Mail,
  X
} from 'lucide-react';

interface FaqItem {
  id: string;
  category: 'umum' | 'teknis' | 'penerbitan';
  categoryLabel: string;
  question: string;
  answer: string;
}

export const FaqSection: React.FC = () => {
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({
    'faq-1': true, // Open first by default
    'faq-2': false,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<'all' | 'umum' | 'teknis' | 'penerbitan'>('all');

  const faqs: FaqItem[] = [
    {
      id: 'faq-1',
      category: 'umum',
      categoryLabel: 'Umum & Konsep',
      question: 'Apa itu UIBN dan apa perbedaannya dengan sistem penomoran konvensional?',
      answer:
        'UIBN (Universal Indonesian Book Number) adalah sistem identifikasi buku digital modern di Indonesia. Berbeda dengan penomoran konvensional yang sering membutuhkan verifikasi manual berhari-hari dan kuota terbatas, UIBN menghadirkan generator kode instan real-time dengan algoritma Modulo-10 checksum, serta pembuatan langsung barcode 1D (Code128 / EAN13) dan QR code 2D yang dapat diverifikasi siapa pun secara terbuka.',
    },
    {
      id: 'faq-2',
      category: 'penerbitan',
      categoryLabel: 'Penerbitan',
      question: 'Siapa saja yang berhak mendaftarkan buku ke dalam sistem UIBN?',
      answer:
        'Sistem UIBN terbuka untuk seluruh insan perbukuan nasional: Penulis mandiri (self-publisher / penulis indie), akademisi dan dosen perguruan tinggi, rumah penerbitan komersial, yayasan, lembaga penelitian, hingga komunitas literasi dan sastra lokal.',
    },
    {
      id: 'faq-3',
      category: 'teknis',
      categoryLabel: 'Format & Teknis',
      question: 'Bagaimana struktur kode UIBN dibentuk dan apakah ada validasi keasliannya?',
      answer:
        'Nomor UIBN tersusun dalam pola terstandardisasi: UIBN-[KodeNegara]-[KodePenerbit]-[NomorUrutJudul]-[DigitChecksum]. Contoh: UIBN-979-8042-1001-9. Digit terakhir dihitung secara matematis menggunakan algoritma Modulo-10 checksum untuk mencegah pemalsuan kode serta mendeteksi saltik (typo) saat input manual.',
    },
    {
      id: 'faq-4',
      category: 'teknis',
      categoryLabel: 'Format & Teknis',
      question: 'Apakah saya langsung mendapatkan Barcode dan QR Code setelah naskah didaftarkan?',
      answer:
        'Ya! Seketika setelah formulir buku disimpan, sistem otomatis membuat Barcode 1D (Code128 / EAN13) dan QR Code 2D resolusi tinggi. Anda dapat langsung mengunduhnya dalam format SVG (vektor tajam tanpa pecah untuk percetakan sampul) maupun PNG resolusi tinggi secara gratis.',
    },
    {
      id: 'faq-5',
      category: 'penerbitan',
      categoryLabel: 'Penerbitan & Biaya',
      question: 'Apakah pendaftaran buku di platform UIBN dipungut biaya?',
      answer:
        'Tidak ada biaya pendaftaran. Platform UIBN dirancang sebagai fasilitas teknologi terbuka untuk mendorong kemajuan literasi nasional, perlindungan hak cipta digital, dan efisiensi industri penerbitan di Indonesia.',
    },
    {
      id: 'faq-6',
      category: 'umum',
      categoryLabel: 'Umum & Konsep',
      question: 'Apakah buku ber-UIBN juga boleh memiliki nomor ISBN konvensional?',
      answer:
        'Tentu saja. UIBN dirancang berdampingan dan kompatibel penuh. Jika buku Anda telah memiliki nomor ISBN dari lembaga lain, Anda dapat mencantumkannya pada formulir pendaftaran untuk melengkapi metadata katalog buku.',
    },
    {
      id: 'faq-7',
      category: 'teknis',
      categoryLabel: 'Format & Teknis',
      question: 'Bagaimana cara pembaca atau toko buku memverifikasi keaslian buku ber-UIBN?',
      answer:
        'Sangat mudah: buka fitur "Pindai Barcode / QR" di aplikasi UIBN menggunakan kamera perangkat apa pun, atau masukkan kode UIBN pada kolom pencarian di halaman utama. Layar akan menampilkan metadata resmi, penerbit, nomor edisi, dan status keaslian buku secara instan.',
    },
    {
      id: 'faq-8',
      category: 'penerbitan',
      categoryLabel: 'Penerbitan',
      question: 'Bagaimana cara penerbit memperoleh Kode Penerbit (Publisher Code) 4-digit?',
      answer:
        'Setiap akun penerbit yang terdaftar di sistem UIBN akan dialokasikan prefiks numerik unik 4-digit (misalnya: 8042, 9103). Kode ini akan secara otomatis melekat pada setiap judul buku yang diterbitkan oleh rumah terbitan tersebut.',
    },
    {
      id: 'faq-9',
      category: 'teknis',
      categoryLabel: 'Format & Teknis',
      question: 'Apakah seluruh data katalog buku dapat diekspor ke aplikasi lain?',
      answer:
        'Ya. Penerbit dan administrator dapat mengekspor seluruh katalog buku ke dalam file CSV berstandar Excel (UTF-8 BOM) dalam satu klik untuk integrasi sistem gudang, distributor, atau toko buku.',
    },
    {
      id: 'faq-10',
      category: 'umum',
      categoryLabel: 'Umum & Konsep',
      question: 'Apakah metadata naskah (judul, sinopsis, halaman) dapat diedit setelah disimpan?',
      answer:
        'Penulis dan penerbit pemilik buku dapat memperbarui informasi metadata seperti sinopsis, jumlah halaman, dan kategori DDC melalui menu pengelolaan buku di dashboard kapan saja.',
    },
    {
      id: 'faq-11',
      category: 'penerbitan',
      categoryLabel: 'Penerbitan & Portofolio',
      question: 'Apa itu Halaman Publik Penulis & Penerbit di UIBN dan bagaimana cara membagikannya?',
      answer:
        'Halaman Publik adalah fitur portofolio digital gratis untuk setiap Penulis dan Penerbit terdaftar di UIBN. Halaman ini memuat seluruh naskah terbit terverifikasi, statistik karya, e-sertifikat, serta tombol bagikan cepat (WhatsApp, Salin Tautan, QR Code Publik, dan Kode Widget Embed untuk dipasang di website pribadi Anda). Anda dapat mengaksesnya via menu profil "Halaman Publik UIBN Saya" atau membuka tautan direktori publik.',
    },
  ];

  const toggleItem = (id: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const filteredFaqs = faqs.filter((faq) => {
    const matchesCategory =
      activeCategory === 'all' || faq.category === activeCategory;
    const matchesQuery =
      searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesQuery;
  });

  return (
    <section id="section-faq-uibn" className="w-full max-w-7xl 2xl:max-w-[1536px] 3xl:max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-10 space-y-10 2xl:space-y-12">
      {/* Section Header */}
      <div className="text-center max-w-3xl 2xl:max-w-4xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs sm:text-sm 2xl:text-base font-semibold shadow-2xs">
          <HelpCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 2xl:w-5 2xl:h-5 text-indigo-600" />
          <span>Pusat Informasi & Bantuan</span>
        </div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl 2xl:text-5xl font-extrabold text-slate-900 tracking-tight leading-tight">
          Pertanyaan yang Sering Diajukan (FAQ)
        </h2>
        <p className="text-sm sm:text-base md:text-lg 2xl:text-xl text-slate-600 leading-relaxed">
          Temukan jawaban cepat seputar konsep UIBN, cara pendaftaran naskah, teknologi barcode & QR code, hingga verifikasi buku di lapangan.
        </p>
      </div>

      {/* Search and Category Filters */}
      <div className="space-y-4 max-w-3xl 2xl:max-w-4xl mx-auto">
        {/* Search Input */}
        <div className="relative">
          <Search className="w-4 h-4 2xl:w-5 2xl:h-5 text-slate-400 absolute left-3.5 2xl:left-4 top-1/2 -translate-y-1/2" />
          <input
            id="input-faq-search"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cari pertanyaan... (misal: barcode, biaya, penerbit, checksum)"
            className="w-full pl-10 2xl:pl-12 pr-9 2xl:pr-10 py-2.5 2xl:py-3 rounded-xl 2xl:rounded-2xl border border-slate-200 text-xs sm:text-sm 2xl:text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition bg-white"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X className="w-3.5 h-3.5 2xl:w-4 2xl:h-4" />
            </button>
          )}
        </div>

        {/* Category Pills */}
        <div className="flex items-center justify-center gap-2 2xl:gap-2.5 flex-wrap text-xs sm:text-sm 2xl:text-base font-semibold">
          <button
            type="button"
            id="btn-faq-cat-all"
            onClick={() => setActiveCategory('all')}
            className={`px-4 sm:px-5 2xl:px-6 py-2 sm:py-2.5 rounded-xl transition cursor-pointer min-h-[44px] flex items-center justify-center ${
              activeCategory === 'all'
                ? 'bg-indigo-600 text-white shadow-2xs'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            Semua Topik ({faqs.length})
          </button>
          <button
            type="button"
            id="btn-faq-cat-umum"
            onClick={() => setActiveCategory('umum')}
            className={`px-4 sm:px-5 2xl:px-6 py-2 sm:py-2.5 rounded-xl transition cursor-pointer min-h-[44px] flex items-center justify-center ${
              activeCategory === 'umum'
                ? 'bg-indigo-600 text-white shadow-2xs'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            Umum & Konsep
          </button>
          <button
            type="button"
            id="btn-faq-cat-teknis"
            onClick={() => setActiveCategory('teknis')}
            className={`px-4 sm:px-5 2xl:px-6 py-2 sm:py-2.5 rounded-xl transition cursor-pointer min-h-[44px] flex items-center justify-center ${
              activeCategory === 'teknis'
                ? 'bg-indigo-600 text-white shadow-2xs'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            Format & Teknis
          </button>
          <button
            type="button"
            id="btn-faq-cat-penerbitan"
            onClick={() => setActiveCategory('penerbitan')}
            className={`px-4 sm:px-5 2xl:px-6 py-2 sm:py-2.5 rounded-xl transition cursor-pointer min-h-[44px] flex items-center justify-center ${
              activeCategory === 'penerbitan'
                ? 'bg-indigo-600 text-white shadow-2xs'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            Penerbitan & Biaya
          </button>
        </div>
      </div>

      {/* Accordion List */}
      <div className="max-w-4xl 2xl:max-w-5xl 3xl:max-w-6xl mx-auto space-y-3.5 2xl:space-y-4">
        {filteredFaqs.length === 0 ? (
          <div className="p-8 text-center bg-white rounded-3xl border border-slate-200 text-slate-500 text-xs sm:text-sm">
            <HelpCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="font-semibold text-slate-700">Tidak ada pertanyaan yang sesuai</p>
            <p className="mt-1">Coba gunakan kata kunci pencarian yang lain atau reset filter kategori.</p>
          </div>
        ) : (
          filteredFaqs.map((faq, fIdx) => {
            const isOpen = !!openItems[faq.id];
            return (
              <div
                key={`faq-acc-${faq.id}-${fIdx}`}
                className={`bg-white rounded-2xl border transition-all duration-200 overflow-hidden ${
                  isOpen
                    ? 'border-indigo-300 shadow-xs ring-2 ring-indigo-500/10'
                    : 'border-slate-200/90 shadow-2xs hover:border-slate-300'
                }`}
              >
                <button
                  type="button"
                  id={`btn-toggle-${faq.id}`}
                  onClick={() => toggleItem(faq.id)}
                  aria-expanded={isOpen}
                  className="w-full text-left p-5 sm:p-6 flex items-start justify-between gap-4 cursor-pointer"
                >
                  <div className="space-y-1">
                    <span className="text-[10px] sm:text-xs 2xl:text-sm font-bold uppercase tracking-wider text-indigo-600 block">
                      {faq.categoryLabel}
                    </span>
                    <h3 className="text-base sm:text-lg 2xl:text-xl font-bold text-slate-900 leading-snug">
                      {faq.question}
                    </h3>
                  </div>
                  <div
                    className={`w-8 h-8 2xl:w-9 2xl:h-9 rounded-xl flex items-center justify-center shrink-0 transition-transform duration-200 ${
                      isOpen
                        ? 'bg-indigo-600 text-white rotate-180'
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                  >
                    <ChevronDown className="w-4 h-4 2xl:w-5 2xl:h-5" />
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 sm:px-6 pb-6 pt-2 text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-600 leading-relaxed border-t border-slate-100 bg-slate-50/40">
                        <p>{faq.answer}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })
        )}
      </div>

      {/* Still Have Questions Box */}
      <div className="p-6 sm:p-8 2xl:p-10 rounded-3xl bg-slate-100/70 border border-slate-200 text-center max-w-2xl 2xl:max-w-3xl 3xl:max-w-4xl mx-auto space-y-3">
        <h4 className="text-base sm:text-lg lg:text-xl 2xl:text-2xl font-bold text-slate-900">
          Masih memiliki pertanyaan lain seputar UIBN?
        </h4>
        <p className="text-xs sm:text-sm lg:text-base 2xl:text-[17px] text-slate-600 leading-relaxed">
          Tim UIBN siap membantu kendala teknis penomoran, integrasi API, atau pendaftaran penerbitan Anda.
        </p>
        <div className="pt-2 flex items-center justify-center gap-2">
          <a
            href="mailto:support@uibn.web.id"
            className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-800 text-xs sm:text-sm 2xl:text-base font-semibold shadow-2xs transition"
          >
            <Mail className="w-4 h-4 2xl:w-5 2xl:h-5 text-indigo-600" />
            <span>Hubungi support@uibn.web.id</span>
          </a>
        </div>
      </div>

      {/* Dynamic Schema.org FAQPage JSON-LD for Search Engine Rich Snippets */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map((faq) => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          }),
        }}
      />
    </section>
  );
};
